<script lang="ts">
  import { onDestroy, onMount } from "svelte";
  import { Hor, Spacer, Ver } from "$components";
  import type {
    Debtor,
    DebtorDeliveryMethod,
    EInvoiceBank,
    EInvoiceCompany,
  } from "../../../../apis/parkito";
  import { Form, Validate } from "../../../lib/validation_next";
  import ValSelect from "../../ValSelect.svelte";
  import ValInputField from "../../ValInputField.svelte";
  import CustomButton from "../../CustomButton.svelte";
  import { Toast } from "$components";
  import {
    deliveryMethodIsEInvoiceCompany,
    deliveryMethodIsEInvoicePerson,
    deliveryMethodIsEmail,
    deliveryMethodIsMail,
    deliveryMethodIsSms,
    getDeliveryMethodType,
  } from "../../../utils/validation";

  export let deliveryMethod: DebtorDeliveryMethod | undefined | null =
    undefined;
  export let debtor: Debtor | undefined | null = undefined;
  export let submitForm: (params: DebtorDeliveryMethod) => void;

  // Delivery method
  const deliveryMethodChoices = [
    { label: "-", value: "none", disabled: false },
    //{ label: "Kivra", value: "kivra", disabled: false },
    { label: "Post", value: "mail", disabled: false },
    { label: "E-post", value: "email", disabled: false },
    { label: "SMS", value: "sms", disabled: false },
    { label: "E-faktura privat", value: "eInvoicePerson", disabled: false },
    {
      label: "E-faktura företag",
      value: "eInvoiceCompany",
      disabled: false,
    },
  ];

  // iInvoicePerson values
  const eInvoicePersonValues: {
    label: string;
    value: EInvoiceBank;
    disabled: false;
  }[] = [
    { label: "Danske Bank", value: "oeb", disabled: false },
    { label: "SEB", value: "seb", disabled: false },
    { label: "Handelsbanken", value: "shb", disabled: false },
    { label: "SkandiaBanken", value: "skb", disabled: false },
    {
      label: "Swedbank och övriga Sparbanker",
      value: "fspa",
      disabled: false,
    },
    { label: "Nordea", value: "nb", disabled: false },
    { label: "Länsförsäkringar", value: "lfb", disabled: false },
    { label: "Sparbanken Finn", value: "finn", disabled: false },
    { label: "ICA Banken", value: "ica", disabled: false },
    { label: "Sparbanken Syd", value: "syd", disabled: false },
    { label: "DnB Nor", value: "dnb", disabled: false },
    { label: "Swedbank (företag)", value: "sbf", disabled: false },
    { label: "Ålandsbanken", value: "aab", disabled: false },
    { label: "Danske Bank (företag)", value: "dbf", disabled: false },
    { label: "SEB (företag)", value: "sebf", disabled: false },
    { label: "Handelsbanken (företag)", value: "shbf", disabled: false },
    { label: "Nordea (företag)", value: "nbf", disabled: false },
    { label: "Forex Bank", value: "frx", disabled: false },
    { label: "Marginalen Bank", value: "marg", disabled: false },
  ];

  // eInvoiceCompany values
  const eInvoiceCompanyValues: {
    label: string;
    value: EInvoiceCompany["intermediator"];
    disabled: false;
  }[] = [
    { label: "OpusCapita", value: "itella", disabled: false },
    { label: "Tieto", value: "tietose", disabled: false },
    { label: "CGI", value: "logica", disabled: false },
    { label: "Visma Proceedo AB", value: "proceedo", disabled: false },
    { label: "Palette", value: "proceedo", disabled: false },
    { label: "Basware", value: "basware", disabled: false },
    { label: "Evry", value: "edb", disabled: false },
    { label: "Strålfors Svenska AB", value: "stralfors1", disabled: false },
    { label: "Liaison Technologies", value: "liasionFi", disabled: false },
    { label: "Readsoft/Lexmark", value: "export", disabled: false },
    { label: "SEB", value: "essesess", disabled: false },
    { label: "Handelsbanken", value: "handsess", disabled: false },
    { label: "Danske bank", value: "dabasess", disabled: false },
    { label: "Swedbank", value: "swedsess", disabled: false },
    { label: "Nordea", value: "ndeasess", disabled: false },
    { label: "InExchange", value: "inExchange", disabled: false },
    { label: "Scancloud", value: "scancloud", disabled: false },
    { label: "Pagero", value: "pagero", disabled: false },
    { label: "Crediflow", value: "crediflow", disabled: false },
    { label: "PEPPOL", value: "peppol", disabled: false },
    { label: "Compello", value: "compello", disabled: false },
    { label: "Logiq AS", value: "logiq", disabled: false },
    { label: "Apix Messaging AB", value: "apix", disabled: false },
    {
      label: "Aksesspunkt Norge AS",
      value: "aksesspunkt",
      disabled: false,
    },
    { label: "Finvoice", value: "fininvoice", disabled: false },
  ];

  const deliveryMethodIndex = deliveryMethod
    ? deliveryMethodChoices.findIndex(
      (choice) => choice.value === getDeliveryMethodType(deliveryMethod),
    )
    : 0;

  const deliveryMethodSelect = Form.multiSelect(
    deliveryMethodChoices,
    deliveryMethodChoices[deliveryMethodIndex],
  );
  const mailAddressField = Form.field(
    Validate.required().and(Validate.len(0, 64)),
    deliveryMethodIsMail(deliveryMethod) ? deliveryMethod.mail.address : "",
  );
  const mailCityField = Form.field(
    Validate.required().and(Validate.len(0, 64)),
    deliveryMethodIsMail(deliveryMethod) ? deliveryMethod.mail.city : "",
  );
  const mailZipField = Form.field(
    Validate.required().and(Validate.len(0, 64)),
    deliveryMethodIsMail(deliveryMethod) ? deliveryMethod.mail.zip : "",
  );
  const mailNameField = Form.field(
    Validate.required().and(Validate.len(0, 64)),
    deliveryMethodIsMail(deliveryMethod) ? deliveryMethod.mail.name : "",
  );
  const mailForm = Form.object({
    mail: Form.nest(
      Form.object({
        address: mailAddressField,
        city: mailCityField,
        name: mailNameField,
        zip: mailZipField,
      }),
    ),
  });
  const emailField = Form.field(
    Validate.required().and(
      Validate.len(0, 64).map((s) => ({ email: { email: s } })),
    ),
    deliveryMethodIsEmail(deliveryMethod)
      ? deliveryMethod.email.email
      : (debtor?.contactEmail ?? ""),
  );
  const smsField = Form.field(
    Validate.required().and(
      Validate.len(0, 64).map((s) => ({ sms: { phone: s } })),
    ),
    deliveryMethodIsSms(deliveryMethod) ? deliveryMethod.sms.phone : "",
  );
  const eInvoicePersonSelect = Form.multiSelect(
    eInvoicePersonValues,
    deliveryMethodIsEInvoicePerson(deliveryMethod)
      ? eInvoicePersonValues.find(
        (f) => f.value === deliveryMethod.eInvoicePerson,
      )
      : eInvoicePersonValues[0],
  );

  const eInvoiceCompanySelect = Form.multiSelect(
    eInvoiceCompanyValues,
    deliveryMethodIsEInvoiceCompany(deliveryMethod)
      ? eInvoiceCompanyValues.find(
        (f) => f.value === deliveryMethod.eInvoiceCompany.intermediator,
      )
      : eInvoiceCompanyValues[0],
  );
  const eInvoiceCompanyGlobalIdField = Form.field(
    Validate.len(0, 64),
    deliveryMethodIsEInvoiceCompany(deliveryMethod) &&
      deliveryMethod.eInvoiceCompany.globalId
      ? deliveryMethod.eInvoiceCompany.globalId
      : "",
  );

  const eInvoiceCompanyForm = Form.object({
    eInvoiceCompany: Form.nest(
      Form.object({
        globalId: eInvoiceCompanyGlobalIdField,
        intermediator: eInvoiceCompanySelect.map((bank) => bank),
      }),
    ),
  });

  const deliveryMethodFields = {
    none: Form.fixed("none" as DebtorDeliveryMethod),
    kivra: Form.fixed("kivra" as DebtorDeliveryMethod),
    mail: Form.nest(mailForm),
    email: emailField,
    sms: smsField,
    eInvoicePerson: eInvoicePersonSelect.map((bank) => ({
      eInvoicePerson: bank,
    })),
    eInvoiceCompany: Form.nest(eInvoiceCompanyForm),
  };
  const form = Form.oneOf(deliveryMethodSelect, deliveryMethodFields);

  onMount(() => {});

  onDestroy(() => {});

  const requestFormSubmit = async () => {
    if (!$form.res.isOk()) {
      Toast.error("Formuläret innehåller fel");
      return;
    }
    const formdata = $form.res.value;

    submitForm(formdata);
  };
</script>

<Ver>
  <ValSelect
    name="delivery-method"
    field={deliveryMethodSelect}
  />
  {#if     $deliveryMethodSelect.res.isOk() &&
      $deliveryMethodSelect.res.value === "mail"}
    <ValInputField
      name="mail-name"
      field={mailNameField}
      label="Namn*"
      placeholder="Namn"
    />
    <ValInputField
      name="mail-address"
      field={mailAddressField}
      label="Adress*"
      placeholder="Adress"
    />
    <ValInputField
      name="mail-zip"
      field={mailZipField}
      label="Postnummer*"
      placeholder="Postnummer"
    />
    <ValInputField
      name="mail-city"
      field={mailCityField}
      label="Stad*"
      placeholder="Stad"
    />
  {:else if     $deliveryMethodSelect.res.isOk() &&
      $deliveryMethodSelect.res.value === "email"}
    <ValInputField
      name="email"
      field={deliveryMethodFields.email}
      label="E-post för faktura*"
      placeholder="E-post"
    />
  {:else if     $deliveryMethodSelect.res.isOk() &&
      $deliveryMethodSelect.res.value === "sms"}
    <ValInputField
      name="sms"
      field={deliveryMethodFields.sms}
      label="Telefonnummer för SMS*"
      placeholder="Ange ett mobilnummer"
    />
  {:else if     $deliveryMethodSelect.res.isOk() &&
      $deliveryMethodSelect.res.value === "eInvoicePerson"}
    <ValSelect
      name="e-invoice-person"
      field={eInvoicePersonSelect}
    />
  {:else if     $deliveryMethodSelect.res.isOk() &&
      $deliveryMethodSelect.res.value === "eInvoiceCompany"}
    <ValSelect
      name="e-invoice-company"
      field={eInvoiceCompanySelect}
    />
    <ValInputField
      name="e-invoice-company-global-id"
      field={eInvoiceCompanyGlobalIdField}
      label="Global ID"
      placeholder="Global ID"
    />
  {/if}

  <CustomButton
    disabled={!($form.res.isOk() && $form.hasChanged)}
    type="primary"
    onClick={requestFormSubmit}
  >Uppdatera</CustomButton>
</Ver>
